import { useCallback, useState } from 'react';
import agent from 'api/agent';
import globalStore from 'store/globalStore';
import { Positions } from 'constants/enums';
import { IdName } from '../../restaurantsPage/components/NewRestaurantDialog';
import { UserData } from 'store/usersStore/reducer';

export function useGroupForm() {
    const [users, setUsers] = useState<IdName[]>([]);
    const [menus, setMenus] = useState<IdName[]>([]);

    const getUsers = useCallback(async (restaurantIds: string[]) => {
        try {
            if (!restaurantIds?.length) return [];
            const response: UserData[] = await agent.Users.GetRestaurantsUsers(restaurantIds, [
                Positions.MANAGER,
            ]);
            return response.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` }));
        } catch (e: any) {
            globalStore.actionCreators.showToaster('error', e);
            return [];
        }
    }, []);

    const getMenus = useCallback(async (restaurantIds: string[]) => {
        if (!restaurantIds?.length) return [];
        return agent.Menus.GetRestaurantsMenus(restaurantIds).catch((e) => {
            globalStore.actionCreators.showToaster('error', e);
            return [];
        });
    }, []);

    const handleRestaurantsOnChange = useCallback(
        async (selected: IdName[]) => {
            const ids = selected.map((r) => r.id);
            const availableUsers = await getUsers(ids);
            const availableMenus = await getMenus(ids);
            setUsers(availableUsers);
            setMenus(availableMenus);
        },
        [getUsers, getMenus]
    );

    const handleRestaurantRemove = useCallback(
        async (
            removed: IdName,
            selected: IdName[],
            selectedUsers: IdName[],
            selectedMenus: IdName[],
            setFieldValue: (field: string, value: any) => void
        ) => {
            const filtered = selected.filter((r) => r.id !== removed.id);
            const availableUsers = filtered.length ? await getUsers(filtered.map((v) => v.id)) : [];
            const filteredUsers = availableUsers.filter((u) =>
                selectedUsers.some((su) => su.id === u.id)
            );
            const availableMenus = filtered.length ? await getMenus(filtered.map((v) => v.id)) : [];
            const filteredMenus = availableMenus.filter((m) =>
                selectedMenus.some((s) => s.id === m.id)
            );
            setUsers(availableUsers);
            setMenus(availableMenus);
            setFieldValue('selectedRestaurants', filtered);
            setFieldValue('selectedUsers', filteredUsers);
            setFieldValue('selectedMenus', filteredMenus);
        },
        [getUsers]
    );

    const handleUserRemove = useCallback(
        (
            removed: IdName,
            selected: IdName[],
            setFieldValue: (field: string, value: any) => void
        ) => {
            setFieldValue(
                'selectedUsers',
                selected.filter((u) => u.id !== removed.id)
            );
        },
        []
    );

    return {
        users,
        menus,
        setUsers,
        handleRestaurantsOnChange,
        handleRestaurantRemove,
        handleUserRemove,
    };
}
